import DropDownChevron from '@assets/icons/dropdown-chevron.svg';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Button, Size } from './button';
import { createStyles } from '@/contexts/theme-context';
import {
  Modal,
  StyleProp,
  TextStyle,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import { shadow } from '@/styles';
import { TrackingData } from '@/utils/analytics/base-reporter';

interface DropdownMenuProps {
  children: React.ReactNode;
  dropdownWidth?: number;
  handleClose: () => void;
  handleOpen: () => void;
  hoverStyle?: StyleProp<ViewStyle>;
  menuStyle?: StyleProp<ViewStyle>;
  menuTitle: string;
  modalOverlayStyle?: StyleProp<ViewStyle>;
  rightElement?: ReactNode;
  rightElementStyle?: StyleProp<ViewStyle>;
  size: Size;
  style: StyleProp<ViewStyle>;
  textStyle: StyleProp<TextStyle>;
  trackingData: TrackingData | null;
  visible: boolean;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  visible,
  handleOpen,
  handleClose,
  hoverStyle,
  menuStyle,
  menuTitle,
  modalOverlayStyle,
  children,
  rightElement,
  rightElementStyle,
  size,
  style,
  textStyle,
  trackingData,
  dropdownWidth = 150,
}) => {
  const triggerRef = useRef<View>(null);
  const [position, setPosition] = useState({ width: 0, x: 0, y: 0 });
  const [styles, theme] = useStyles();

  useEffect(() => {
    if (triggerRef.current && visible) {
      triggerRef.current.measure((_fx, _fy, width, height, px, py) => {
        setPosition({
          width: width,
          x: px,
          y: py + height,
        });
      });
    }
  }, [visible]);

  return (
    <>
      <Button
        hoverStyle={hoverStyle}
        onPress={() => {
          if (visible) {
            handleClose();
          } else {
            handleOpen();
          }
        }}
        ref={triggerRef}
        rightElement={
          rightElement ? (
            rightElement
          ) : (
            <DropDownChevron stroke={theme.colors.white} />
          )
        }
        rightElementStyle={rightElementStyle ?? styles.chevron}
        size={size}
        style={style}
        textStyle={textStyle}
        trackingData={trackingData}
      >
        {menuTitle}
      </Button>
      {visible ? (
        <Modal
          animationType="fade"
          onRequestClose={handleClose}
          testID="dropdown-modal"
          transparent
          visible={visible}
        >
          <TouchableWithoutFeedback onPress={handleClose}>
            <View style={[styles.modalOverlay, modalOverlayStyle]}>
              <View
                style={[
                  styles.menu,
                  {
                    left: position.x + position.width / 2 - dropdownWidth / 2,
                    top: position.y,
                    width: dropdownWidth,
                  },
                  menuStyle,
                ]}
              >
                {children}
              </View>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  chevron: {
    marginLeft: 8,
  },
  menu: {
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    elevation: 5,
    fontFamily: theme.fonts.primary,
    paddingHorizontal: 12,
    paddingVertical: 8,
    position: 'absolute',
    ...shadow({
      color: theme.colors.black,
      elevation: 5,
      opacity: 0.2,
      radius: 4,
      x: 0,
      y: 2,
    }),
  },
  modalOverlay: {
    alignItems: 'flex-start',
    backgroundColor: theme.colors.transparent,
    flex: 1,
    justifyContent: 'flex-start',
    marginTop: 8,
  },
}));
